import { MediaType } from '@confluence/graphql/entry-points/MediaType';
import { useExperimentalMutation } from '@confluence/legacy-schema-bridge/entry-points/useExperimentalMutation';
import { getOnContentUpdatedObjFromResponse } from '@confluence/graphql/entry-points/sharedTemplateBlueprintReqFns';
import { BadStatusError } from '@confluence/network';
import { expValEquals } from '@confluence/feature-experiments';

import type {
	UpdateDraftByBlueprintMutationVariables,
	UpdateDraftByBlueprintMutation_experimentalUpdateDraftWithBlueprint_content__links as ContentLinks,
	UpdateDraftByBlueprintMutation_experimentalUpdateDraftWithBlueprint_content_metadata as ContentMetadata,
} from '../queries/__types__/UpdateDraftByBlueprintMutation';

export const useExperimentalUpdateDraftWithBlueprintMutation = () => {
	const expandParam = expValEquals(
		'cc_fix_deprecated_editor_property_for_template',
		'isEnabled',
		true,
	)
		? 'space.key,metadata.simple.isFabric'
		: 'space.key,metadata.properties.editor';

	return useExperimentalMutation<
		UpdateDraftByBlueprintResponse,
		UpdateDraftByBlueprintMutationVariables,
		UpdateDraftByBlueprintApiResponse
	>({
		url: `/wiki/rest/create-dialog/1.0/content/blueprint/instance?expand=${expandParam}`,
		method: 'PUT',
		headers: {
			'Content-Type': MediaType.JSON,
		},
		body: ({ contentId, spaceKey, blueprint, parentPageId, title }) => {
			const blueprintSpec = JSON.parse(blueprint);

			const content: any = {
				id: contentId,
				type: 'page',
				status: 'draft',
				space: {
					key: spaceKey,
				},
				version: {
					number: 1,
				},
			};

			if (title) {
				content.title = title;
			}

			if (parentPageId && parentPageId !== 'no-parent-page') {
				content.ancestors = [{ id: parentPageId }];
			}

			const pageData = {
				content,
				contentBlueprintSpec: {
					blueprintId: blueprintSpec.contentBlueprintId,
					context: blueprintSpec,
				},
			};

			return JSON.stringify(pageData);
		},
		parseResult: (response) => {
			return {
				experimentalUpdateDraftWithBlueprint: getOnContentUpdatedObjFromResponse(response),
			};
		},
		transformError: async (error) => {
			if (error instanceof BadStatusError && error.response?.status === 500) {
				const backendError = await error.response.json();
				return new BadStatusError(backendError.errorMessage, error.response);
			}
			return error;
		},
		maxRetries: 1,
	});
};

interface UpdateDraftByBlueprintResponse {
	experimentalUpdateDraftWithBlueprint: ExperimentalUpdateDraftWithBlueprint | null;
}

export interface ExperimentalUpdateDraftWithBlueprint {
	content: Content | null;
}

interface Content {
	id: string | null;
	type: string | null;
	title: string | null;
	subType: string | null;
	space: ContentSpace | null;
	_links: ContentLinks | null;
	metadata: ContentMetadata | null;
}

interface ContentSpace {
	key: string | null;
}

interface UpdateDraftByBlueprintApiResponse {
	content?: {
		id?: string;
		title?: string;
		subType?: string;
		space?: {
			key?: string;
		};
		_links?: {
			editui?: string;
		};
		metadata?: {
			properties?: {
				editor?: {
					value?: string;
				};
			};
			simple?: {
				isFabric?: boolean;
			};
		};
	};
}
